import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { LayoutDashboard, Percent, Users as UsersIcon, Building2, Settings as SettingsIcon, LogOut, Menu, X } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const ROLE_LABEL = { super_admin: "Super Admin", manager: "Manager", user: "User" };

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["super_admin", "manager", "user"] },
  { to: "/commission", label: "Commission", icon: Percent, roles: ["super_admin", "manager", "user"] },
  { to: "/users", label: "Users", icon: UsersIcon, roles: ["super_admin", "manager"] },
  { to: "/departments", label: "Departments", icon: Building2, roles: ["super_admin"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["super_admin"] },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { branding, logoUrl } = useBranding();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = NAV.filter((n) => n.roles.includes(user?.role));
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const sideContent = (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6 border-b border-border flex items-center gap-3">
        {logoUrl ? (
          <img src={logoUrl} alt="logo" className="h-9 w-9 object-contain" data-testid="brand-logo" />
        ) : (
          <div className="h-9 w-9 bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-lg">
            {(branding?.app_name || "M")[0]}
          </div>
        )}
        <div className="leading-tight">
          <div className="font-heading font-semibold tracking-tight">{branding?.app_name || "Meridian"}</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{branding?.tagline || "Commission Suite"}</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {links.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors duration-200 border ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-transparent text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-3 py-4 border-t border-border">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-9 w-9 rounded-none border border-border">
            <AvatarFallback className="rounded-none bg-secondary text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={() => { logout(); navigate("/login"); }}
          data-testid="logout-btn"
          className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:bg-secondary transition-colors"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-border flex-col fixed inset-y-0">
        {sideContent}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-background border-r border-border">
            {sideContent}
          </aside>
        </div>
      )}

      <div className="flex-1 md:ml-64 min-w-0">
        <header className="sticky top-0 z-30 h-16 border-b border-border bg-background/80 backdrop-blur-xl flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Badge variant="outline" className="rounded-none border-border text-[10px] uppercase tracking-[0.12em]" data-testid="role-badge">
              {ROLE_LABEL[user?.role] || user?.role}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground hidden sm:block">
            {new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </div>
        </header>
        <main className="p-4 md:p-8 max-w-[1600px]">
          <ErrorBoundary key={window.location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
